<script lang="ts" setup>
import SimpleProgressRateTable from '@/corePagesModule/progressRateTableContent/pages/SimpleProgressRateTable.vue';
import SummaryCard from '@/corePagesModule/components/SummaryCard.vue';
import { OrganizationBusinessRepository } from '@/businessRepository/organizationBusinessRepository';
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useSupplierForecastStore } from './useSupplierForecastStore';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import { i18n } from '@/i18n';

// 事前準備
const organizationBusinessRepository = new OrganizationBusinessRepository();

// pinia
const supplierForecastStore = useSupplierForecastStore();
const { state } = storeToRefs(supplierForecastStore);
const { fetchState, updateTableData, checkBusiness } = supplierForecastStore;

const t = i18n.t;

onMounted(async () => {
  await fetchState(organizationBusinessRepository);
});

// 年月変更の監視
// 値が変わったら表示するテーブルを選択年月の情報に更新する
watch(
  () => state.value.selectYearMonthInfo,
  async () => {
    // テーブル表示に使用する情報を更新
    await updateTableData();
  },
  {
    deep: true,
  }
);
</script>

<template>
  <div class="supplier-announce-container">
    <div class="supplier-announce-summary-content">
      <div class="summary-area-header-content supplier-announce-summary-content__header">
        <label class="summary-area-header-content__title">
          {{ t('全体サマリ') }}
        </label>
        <div class="summary-area-header-content__year-month-select">
          <label class="summary-area-header-content__year-month-select--label">
            {{ '内示作成月' }}
          </label>
          <MonthPicker
            v-model:model-value="state.selectYearMonthInfo"
            class="summary-area-header-content__year-month-select--select"
            :locale="'ja'"
          />
        </div>
      </div>
      <div class="summary-area-body-container supplier-announce-summary-content__summary-area">
        <div class="summary-area-body-content">
          <router-link :to="{ name: 'all-businesses' }">
            <SummaryCard
              :numerator="state.achieveCount"
              :denominator="state.totalCount"
              :detail-info="'全取引'"
              :display-detail-item-type="'label'"
            />
          </router-link>
          <SummaryCard
            :numerator="state.achieveNonResponsiveBusinessCount"
            :denominator="state.totalNonResponsiveBusinessCount"
            :detail-info="'未割振取引'"
            :display-detail-item-type="'label'"
          />
        </div>
      </div>
    </div>
    <div class="supplier-announce-table-area-container">
      <div class="supplier-announce-table-area-content">
        <div class="supplier-announce-table-area-header-container supplier-announce-table-content__header">
          <div class="supplier-announce-table-area-header-content">
            <!-- タブコンポーネントを挿入する -->
            <!-- <ControlSupplierPageTab :tab-info-object="state.routerInfo" /> -->
            <button @click="checkBusiness()">
              {{ '確認用' }}
            </button>
          </div>
        </div>
        <div class="supplier-announce-table-area-body-container supplier-announce-table-content__body">
          <div class="supplier-announce-table-area-body-container__table--left">
            <SimpleProgressRateTable
              :table-type="'progressRateTableByRepPerson'"
              :items="state.displayUserBusinessList"
              :scroll-enable="true"
            />
          </div>
          <div class="supplier-announce-table-area-body-container__table--right">
            <SimpleProgressRateTable
              :table-type="'progressRateTableBySupplier'"
              :items="state.displaySupplierBusinessList"
              :scroll-enable="true"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.supplier-announce-container {
  display: grid;
  grid-template-columns: minmax(1508px, 1fr);
  grid-template-rows: minmax(273px, 0.345fr) minmax(502px, 0.635fr);
  height: 100%;
  row-gap: 16px;
}
.supplier-announce-summary-content {
  border: 1px solid;
  border-radius: 4px;
  border-color: $border;
  background-color: $semanticWhite;
  padding: 0px 24px 0px 16px;
}

.summary-area-header-content {
  display: grid;
  grid-template-columns: minmax(80px, 0.185fr) minmax(220px, 0.34fr) minmax(1152px, 0.78fr);
  grid-template-rows: minmax(36px, 1fr);
  padding: 8px 0px 8px 0px;
  column-gap: 32px;
  &__title {
    width: auto;
    height: 16px;
    font-weight: bold;
    font-family: Noto Sans JP;
    font-size: 16px;
    line-height: 16px;
    white-space: nowrap;
    margin: auto 0px auto 0px;
  }
}
.summary-area-header-content__year-month-select {
  display: grid;
  grid-template-columns: minmax(70px, 0.318fr) minmax(142px, 0.645fr);
  grid-template-rows: minmax(36px, 1fr);
  column-gap: 8px;
  &--label {
    width: 70px;
    height: 20px;
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    margin: auto 0px auto 0px;
  }
  /* selectはコンポーネント使うから後で消す */
  &--select {
    width: 142px;
    height: 36px;
    border-radius: 4px;
    border: 1px solid;
    border-color: $border;
    background-color: $semanticWhite;
  }
}
.summary-area-body-container {
  padding: 8px 0px 8px 0px;
}
.summary-area-body-content {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  grid-template-rows: minmax(173px, 1fr);
  column-gap: 8px;
  /* サマリコンポーネントに移植する */
  &__component-1 {
    width: 132px;
    height: 173px;
    padding: 8px 0px 16px 0px;
    border-radius: 4px;
    border: 1px solid;
    border-color: $border;
    background-color: $semanticWhite;
    margin-right: 8px;
  }
  &__component-2 {
    width: 132px;
    height: 173px;
    padding: 8px 0px 16px 0px;
    border-radius: 4px;
    border: 1px solid;
    border-color: $border;
    background-color: $semanticWhite;
  }
}

.supplier-announce-table-area-container {
  border: 1px solid;
  border-radius: 4px;
  border-color: $border;
  background-color: $semanticWhite;
}
.supplier-announce-table-area-content {
  padding: 24px 16px 24px 16px;
}
.supplier-announce-table-area-header-container {
  height: 30px;
  width: 100%;
  padding: 8px 0px 8px 0px;
  margin-bottom: 16px;
}
.supplier-announce-table-area-body-container {
  height: 408px;
  width: 100%;
  display: grid;
  grid-template-columns: minmax(730px, 0.5fr) minmax(730px, 0.5fr);
  grid-template-rows: minmax(408px, 1fr);
  column-gap: 16px;
  &__table--left {
    border-radius: 4px;
    border: 1px soli;
    background-color: $backgroundDefault;
  }
  &__table--right {
    border-radius: 4px;
    border: 1px soli;
    background-color: $backgroundDefault;
  }
}
</style>
